import { EntityStatus, EntitySubStatus, PARENT_IDENTITY_STATUS_SETS } from './entity.status';
import { Party } from './enum';
import { getVisibleReasons, isStartingStatus } from './status.settings.rules';

/** A status as a picker offers it. Assignable to `EntitySubStatus` so the untyped controls keep binding. */
export interface StatusPickerOption extends EntitySubStatus {
    reasonRequired?: boolean;
    selectableBy?: Party[];
    /** Who may still change a record's fields in this status; the portal's evidence gate reads it for the task's own status (WBS-12033). */
    editableBy?: Party[];

    /**
     * What the status change dialog offers when the status asks for a reason.
     *
     * Carried on the option so choosing a status needs no second request, and without the hidden
     * same-named default: that one exists so a stamp is never incomplete, and is what the server writes
     * when nobody is asked.
     */
    reasons?: EntitySubStatus[];
}

export interface StatusPickerFlattenOptions {
    excludeIds?: string[];
    /** Set for a selection control; left unset for a filter, which must list every status. */
    party?: Party;
}

/**
 * Whether this party may move a record into the status (SP-7).
 *
 * Absent means nobody has configured the status, so it stays offered — the thirteen sets that keep the
 * group shape carry no list at all, and reading absence as a refusal would empty their pickers. Only an
 * explicitly empty list is system-only.
 */
export function canSelectStatus(status: Pick<EntityStatus, 'selectableBy'> | null | undefined, party: Party): boolean {

    return !status?.selectableBy || status.selectableBy.includes(party);
}

/**
 * Flattens a status set into picker options, per shape.
 *
 * In a converted set the parent is the status and its sub-statuses are reasons, so flattening the
 * sub-statuses would offer a reason where a status belongs. The thirteen sets that keep the group shape
 * flatten as they always have.
 *
 * `parent.name` is what every consumer groups by, so it carries the group either way: the status type in
 * a converted set, where there is no parent left to name, and the parent's own name in a legacy one.
 * Same rule as the read seam in `EntityStatusService.getSubStatuses`, which these pickers bypass.
 */
export function toStatusPickerOptions(statuses: EntityStatus[], options?: StatusPickerFlattenOptions): StatusPickerOption[] {

    const excluded = new Set(options?.excludeIds ?? []);

    return statuses.flatMap(status => statusToOptions(status, excluded, options?.party));
}

function statusToOptions(status: EntityStatus, excluded: Set<string>, party?: Party): StatusPickerOption[] {

    if (!PARENT_IDENTITY_STATUS_SETS.includes(status.entityType)) {
        return status.subStatuses
            ?.filter(subStatus => !excluded.has(subStatus._id) && subStatus.active !== false)
            .map(subStatus => ({
                ...subStatus,
                parent: { _id: status._id, name: status.name, sortOrder: status.sortOrder },
                statusType: status.statusType,
            })) ?? [];
    }

    if (excluded.has(status._id) || status.active === false) return [];
    if (party && !canSelectStatus(status, party)) return [];

    return [
        {
            _id: status._id,
            name: status.name,
            color: status.color,
            active: status.active,
            sortOrder: status.sortOrder,
            systemName: status.systemName,
            legacyStatus: status.legacyStatus,
            isDefault: status.isDefault,
            statusType: status.statusType,
            reasonRequired: status.reasonRequired,
            selectableBy: status.selectableBy,
            editableBy: status.editableBy,
            // Settings no longer manages reasons on a starting status, so ones stored before stay hidden rather than offered unmanaged.
            reasons: isStartingStatus(status) ? [] : getVisibleReasons(status).filter(reason => reason.active !== false),
            parent: { _id: status._id, name: status.statusType, sortOrder: status.sortOrder },
        } as StatusPickerOption,
    ];
}
